import { useEffect, useState, useCallback } from "react";
import { api, apiError } from "./api";
import { toast } from "sonner";
import { useNotificationStream } from "./useNotificationStream";
import { Bell, Check, CheckCheck, X, Settings2, Inbox } from "lucide-react";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "./components/ui/popover";

// ── severity → icon color mapping ─────────────────────────────────────────
const SEV = {
  info:    { dot: "bg-sky-500",     tint: "bg-sky-50 text-sky-700" },
  success: { dot: "bg-emerald-500", tint: "bg-emerald-50 text-emerald-700" },
  warning: { dot: "bg-amber-500",   tint: "bg-amber-50 text-amber-700" },
  error:   { dot: "bg-rose-500",    tint: "bg-rose-50 text-rose-700" },
};
const sev = (s) => SEV[s] || SEV.info;

function relTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

// ── main bell component ──────────────────────────────────────────────────
export default function NotificationBell({ onOpenTab }) {
  const [count, setCount] = useState(0);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  // Poll unread count every 25s
  const fetchCount = useCallback(async () => {
    try {
      const r = await api.get("/notifications/unread_count");
      setCount(r.data.unread || 0);
    } catch { /* ignore */ }
  }, []);
  useEffect(() => {
    fetchCount();
    const t = setInterval(fetchCount, 25000);
    return () => clearInterval(t);
  }, [fetchCount]);

  // Live SSE: bump badge + prepend to the open list instantly
  const onLive = useCallback((n) => {
    setCount((c) => c + 1);
    setItems((prev) => [n, ...prev].slice(0, 8));
  }, []);
  useNotificationStream(onLive);

  // Load recent 8 when opened
  const loadRecent = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/notifications", { params: { limit: 8 } });
      setItems(r.data.items || []);
      setCount(r.data.unread_count || 0);
    } catch (e) {
      toast.error(apiError(e, "Failed to load notifications"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (open) loadRecent(); }, [open, loadRecent]);

  const markOne = async (id) => {
    setItems((prev) => prev.map((it) => it.id === id ? { ...it, is_read: true, read_at: new Date().toISOString() } : it));
    setCount((c) => Math.max(0, c - 1));
    try { await api.post(`/notifications/${id}/read`); } catch { /* ignore */ }
  };
  const markAll = async () => {
    try {
      await api.post("/notifications/read_all");
      setItems((prev) => prev.map((it) => ({ ...it, is_read: true })));
      setCount(0);
      toast.success("All notifications marked read");
    } catch (e) { toast.error(apiError(e, "Failed")); }
  };

  const openItem = async (n) => {
    if (!n.is_read) await markOne(n.id);
    if (n.link && onOpenTab) {
      const m = n.link.match(/tab=(\w+)/);
      if (m) onOpenTab(m[1]);
    }
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid="notif-bell"
          className="relative h-9 w-9 rounded-lg hover:bg-gray-100 flex items-center justify-center transition-colors"
          title="Notifications"
        >
          <Bell size={18} className="text-gray-700" />
          {count > 0 && (
            <span
              data-testid="notif-bell-count"
              className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center"
            >
              {count > 99 ? "99+" : count}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        data-testid="notif-bell-popover"
        align="end"
        className="w-96 p-0 overflow-hidden"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <Bell size={15} className="text-indigo-600" />
            <span className="font-semibold text-sm text-gray-900">Notifications</span>
            {count > 0 && (
              <span className="text-[11px] font-semibold text-rose-700 bg-rose-100 rounded-full px-2 py-0.5">
                {count} unread
              </span>
            )}
          </div>
          <div className="flex items-center gap-1">
            <button
              data-testid="notif-bell-mark-all"
              onClick={markAll}
              className="text-[11px] font-medium text-indigo-700 hover:bg-indigo-50 rounded px-2 py-1"
              disabled={count === 0}
            >Mark all read</button>
            <button
              data-testid="notif-bell-open-tab"
              onClick={() => { setOpen(false); onOpenTab && onOpenTab("notifications"); }}
              className="text-[11px] font-medium text-gray-600 hover:bg-gray-100 rounded px-2 py-1"
            >View all →</button>
          </div>
        </div>

        <div className="max-h-[420px] overflow-y-auto">
          {loading ? (
            <div className="py-8 text-center text-xs text-gray-400">Loading…</div>
          ) : items.length === 0 ? (
            <div className="py-10 text-center" data-testid="notif-bell-empty">
              <Inbox size={22} className="mx-auto text-gray-300 mb-2" />
              <div className="text-sm font-semibold text-gray-600">You&apos;re all caught up</div>
              <div className="text-[11px] text-gray-400 mt-1">Nothing new right now.</div>
            </div>
          ) : (
            items.map((n) => (
              <button
                key={n.id}
                data-testid={`notif-item-${n.id}`}
                onClick={() => openItem(n)}
                className={`w-full text-left px-4 py-3 border-b border-gray-50 last:border-0 hover:bg-gray-50/60 transition-colors ${n.is_read ? "opacity-70" : ""}`}
              >
                <div className="flex items-start gap-2">
                  <span className={`mt-1.5 w-2 h-2 rounded-full ${sev(n.severity).dot} shrink-0`} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-semibold text-gray-900 truncate">{n.title}</span>
                      <span className="text-[10px] text-gray-400 shrink-0 tabnum">{relTime(n.created_at)}</span>
                    </div>
                    <div className="text-xs text-gray-600 mt-0.5 line-clamp-2">{n.body}</div>
                  </div>
                </div>
              </button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
